import { SCView } from '@lib/sc-eone-ui';
import { Button } from 'eone-ui/buttons/button';
import { useEffect, useState } from 'react';
import {
  DATE_LITERALS,
  addToGroup,
  fieldCategory,
  newCondition,
  newGroup,
  operatorsFor,
  removeById,
  updateById,
  type FilterCondition,
  type FilterGroup,
} from './filters';
import { METADATA_BROWSER_TID, type SalesforceField } from './types';

const TID = `${METADATA_BROWSER_TID}.where`;

interface WhereBuilderProps {
  fields: SalesforceField[]; // filterable base-object fields
  root: FilterGroup;
  onChange: (root: FilterGroup) => void;
}

export function WhereBuilder({ fields, root, onChange }: WhereBuilderProps) {
  const filterable = fields.filter((f) => f.filterable);
  if (filterable.length === 0) {
    return (
      <SCView textDesc textXs>
        No filterable fields on this object.
      </SCView>
    );
  }
  return (
    <SCView flexCol DataTestId={TID}>
      <GroupEditor
        group={root}
        fields={filterable}
        root={root}
        onChange={onChange}
        isRoot
        depth={0}
      />
    </SCView>
  );
}

function GroupEditor({
  group,
  fields,
  root,
  onChange,
  isRoot,
  depth,
}: {
  group: FilterGroup;
  fields: SalesforceField[];
  root: FilterGroup;
  onChange: (root: FilterGroup) => void;
  isRoot: boolean;
  depth: number;
}) {
  const defaultField = fields[0];

  const setCombinator = (c: 'AND' | 'OR') =>
    onChange(
      isRoot
        ? { ...root, combinator: c }
        : updateById(root, group.id, (n) => ({ ...(n as FilterGroup), combinator: c })),
    );
  const addCondition = () =>
    onChange(addToGroup(root, group.id, newCondition(defaultField.apiName, defaultField.type)));
  const addGroup = () => onChange(addToGroup(root, group.id, newGroup('AND')));
  const removeSelf = () => onChange(removeById(root, group.id));

  return (
    <SCView
      flexCol
      gap={2}
      className={isRoot ? 'sfmb-where-root' : 'sfmb-where-group'}
      DataTestId={`${TID}.group.${group.id}`}
    >
      <SCView flex itemsCenter gap={2} flexWrap>
        <div className="sfmb-combi" role="tablist">
          {(['AND', 'OR'] as const).map((c) => (
            <button
              key={c}
              type="button"
              className={`sfmb-combi-btn ${group.combinator === c ? 'is-active' : ''}`}
              onClick={() => setCombinator(c)}
              data-testid={`${TID}.group.${group.id}.combinator.${c}`}
            >
              {c}
            </button>
          ))}
        </div>
        <Button
          Label="+ Condition"
          OnClick={addCondition}
          Class="eone-ui-button-container-secondary"
          DataTestId={`${TID}.group.${group.id}.add-condition`}
        />
        {depth < 3 && (
          <Button
            Label="+ Group"
            OnClick={addGroup}
            Class="eone-ui-button-container-secondary"
            DataTestId={`${TID}.group.${group.id}.add-group`}
          />
        )}
        {!isRoot && (
          <button
            type="button"
            className="sfmb-icon-btn"
            onClick={removeSelf}
            title="Remove group"
            data-testid={`${TID}.group.${group.id}.remove`}
          >
            ×
          </button>
        )}
      </SCView>

      {group.children.length === 0 && (
        <SCView textXs textDesc pl={2}>
          Empty group — add a condition.
        </SCView>
      )}

      <SCView flexCol gap={2} pl={3} className="sfmb-where-children">
        {group.children.map((child) =>
          child.kind === 'group' ? (
            <GroupEditor
              key={child.id}
              group={child}
              fields={fields}
              root={root}
              onChange={onChange}
              isRoot={false}
              depth={depth + 1}
            />
          ) : (
            <ConditionEditor
              key={child.id}
              cond={child}
              fields={fields}
              root={root}
              onChange={onChange}
            />
          ),
        )}
      </SCView>
    </SCView>
  );
}

function ConditionEditor({
  cond,
  fields,
  root,
  onChange,
}: {
  cond: FilterCondition;
  fields: SalesforceField[];
  root: FilterGroup;
  onChange: (root: FilterGroup) => void;
}) {
  const patch = (fn: (c: FilterCondition) => FilterCondition) =>
    onChange(updateById(root, cond.id, (n) => (n.kind === 'condition' ? fn(n) : n)));

  // SC-1337: the multi-value ("in" / "not in") input keeps its own raw text so the user can type
  // commas and spaces freely (incl. multi-word values like "Acme Corp"). Deriving the controlled
  // value from cond.values.join(', ') on every keystroke stripped the separator being typed, making
  // the operators unusable. We parse the raw text into the values array, and only resync the text
  // when the model resets the values externally (field / operator change) — not on each keystroke.
  const [multiText, setMultiText] = useState(() => cond.values.join(', '));
  useEffect(() => {
    setMultiText(cond.values.join(', '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cond.field, cond.operator]);

  const field = fields.find((f) => f.apiName === cond.field);
  const cat = fieldCategory(cond.type);
  const ops = operatorsFor(cat);
  const op = ops.find((o) => o.value === cond.operator) ?? ops[0];
  const multi = !!op?.multi;
  const picklist = field?.picklistValues ?? [];
  const datalistId = `${TID}-dl-${cond.id}`;

  const onFieldChange = (apiName: string) => {
    const f = fields.find((x) => x.apiName === apiName);
    const newCat = fieldCategory(f?.type ?? 'string');
    const firstOp = operatorsFor(newCat)[0]?.value ?? '=';
    patch((c) => ({
      ...c,
      field: apiName,
      type: f?.type ?? 'string',
      operator: firstOp,
      value: '',
      values: [],
    }));
  };
  const onOperatorChange = (operator: string) =>
    patch((c) => ({ ...c, operator, value: '', values: [] }));
  const toggleValues = (v: string) =>
    patch((c) => ({
      ...c,
      values: c.values.includes(v) ? c.values.filter((x) => x !== v) : [...c.values, v],
    }));

  return (
    <SCView
      flex
      itemsCenter
      gap={1}
      flexWrap
      className="sfmb-cond"
      DataTestId={`${TID}.condition.${cond.id}`}
    >
      <select
        className="sfmb-select"
        value={cond.field}
        onChange={(e) => onFieldChange(e.target.value)}
        data-testid={`${TID}.condition.${cond.id}.field`}
      >
        {fields.map((f) => (
          <option key={f.apiName} value={f.apiName}>
            {f.label} ({f.apiName})
          </option>
        ))}
      </select>

      <select
        className="sfmb-select"
        value={cond.operator}
        onChange={(e) => onOperatorChange(e.target.value)}
        data-testid={`${TID}.condition.${cond.id}.op`}
      >
        {ops.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      {/* value input */}
      {multi ? (
        picklist.length > 0 ? (
          <SCView flex flexWrap gap={1} className="sfmb-cond-values">
            {picklist.map((p) => (
              <label key={p.value} className="sfmb-cond-check" title={p.value}>
                <input
                  type="checkbox"
                  checked={cond.values.includes(p.value)}
                  onChange={() => toggleValues(p.value)}
                />
                {p.label || p.value}
              </label>
            ))}
          </SCView>
        ) : (
          <input
            className="sfmb-cond-input"
            placeholder="comma,separated,values"
            value={multiText}
            onChange={(e) => {
              const raw = e.target.value;
              setMultiText(raw);
              patch((c) => ({
                ...c,
                values: raw
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              }));
            }}
            data-testid={`${TID}.condition.${cond.id}.values`}
          />
        )
      ) : cond.valueMode === 'token' ? (
        <SCView flex itemsCenter gap={1} className="sfmb-token">
          <span className="sfmb-mono">[[</span>
          <input
            className="sfmb-cond-input"
            placeholder="token"
            value={cond.value}
            onChange={(e) => patch((c) => ({ ...c, value: e.target.value.replace(/[^\w]/g, '') }))}
            data-testid={`${TID}.condition.${cond.id}.token`}
          />
          <span className="sfmb-mono">]]</span>
        </SCView>
      ) : cat === 'boolean' ? (
        <select
          className="sfmb-select"
          value={cond.value}
          onChange={(e) => patch((c) => ({ ...c, value: e.target.value }))}
          data-testid={`${TID}.condition.${cond.id}.value`}
        >
          <option value="">…</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ) : cat === 'picklist' ? (
        <select
          className="sfmb-select"
          value={cond.value}
          onChange={(e) => patch((c) => ({ ...c, value: e.target.value }))}
          data-testid={`${TID}.condition.${cond.id}.value`}
        >
          <option value="">…</option>
          {picklist.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label || p.value}
            </option>
          ))}
        </select>
      ) : cat === 'date' ? (
        <>
          <input
            className="sfmb-cond-input"
            list={datalistId}
            placeholder="date or literal"
            value={cond.value}
            onChange={(e) => patch((c) => ({ ...c, value: e.target.value }))}
            data-testid={`${TID}.condition.${cond.id}.value`}
          />
          <datalist id={datalistId}>
            {DATE_LITERALS.map((d) => (
              <option key={d} value={d} />
            ))}
          </datalist>
        </>
      ) : (
        <input
          className="sfmb-cond-input"
          type={cat === 'number' ? 'number' : 'text'}
          placeholder={op?.value === 'LIKE' ? '%value%' : 'value'}
          value={cond.value}
          onChange={(e) => patch((c) => ({ ...c, value: e.target.value }))}
          data-testid={`${TID}.condition.${cond.id}.value`}
        />
      )}

      {/* literal / token toggle (single-value operators only) */}
      {!multi && cat !== 'boolean' && (
        <button
          type="button"
          className={`sfmb-seg-btn ${cond.valueMode === 'token' ? 'is-active' : ''}`}
          style={{ flex: '0 0 auto', padding: '2px 6px' }}
          onClick={() =>
            patch((c) => ({
              ...c,
              valueMode: c.valueMode === 'token' ? 'literal' : 'token',
              value: '',
            }))
          }
          title={
            cond.valueMode === 'token'
              ? 'Using a [[token]] — switch to a literal value'
              : 'Switch to a [[token]] runtime placeholder'
          }
          data-testid={`${TID}.condition.${cond.id}.mode`}
        >
          [[ ]]
        </button>
      )}

      <button
        type="button"
        className="sfmb-icon-btn"
        onClick={() => onChange(removeById(root, cond.id))}
        title="Remove condition"
        data-testid={`${TID}.condition.${cond.id}.remove`}
      >
        ×
      </button>
    </SCView>
  );
}
